// ============================================================================
// Vedic Generative Diagrams — pure geometry engine
// ----------------------------------------------------------------------------
// Unlike the "Planning Guide" reports (static PNG images), these 6 reports are
// mathematically DRAWN at render time — same way both in the Konva edit canvas
// and in the jsPDF export — so there is no giant raster file, and the diagram
// is always crisp at any resolution.
//
// All geometry is computed in a normalized 1000 x 1000 unit square
// (center = 500,500, half-size R = 500). Callers scale/translate this into
// whatever pixel box they are drawing into.
//
// Angle convention: degrees, measured CLOCKWISE from North (the up direction).
// angle 0   -> straight up (North)
// angle 90  -> straight right (East)
// angle 180 -> straight down (South)
// angle 270 -> straight left (West)
// ============================================================================

export const VEDIC_GEN_SIZE = 1000;
const CX = 500;
const CY = 500;
const R = 500;

// Point where a ray from center at `angleDeg`, clipped to a square of
// half-size `halfSize` centered at (CX,CY), exits that square's boundary.
export function squareRayPoint(angleDeg, halfSize = R, cx = CX, cy = CY) {
  const rad = (angleDeg * Math.PI) / 180;
  const dx = Math.sin(rad);
  const dy = -Math.cos(rad);
  const tX = Math.abs(dx) > 1e-9 ? halfSize / Math.abs(dx) : Infinity;
  const tY = Math.abs(dy) > 1e-9 ? halfSize / Math.abs(dy) : Infinity;
  const t = Math.min(tX, tY);
  return { x: cx + t * dx, y: cy + t * dy };
}

// The 32-zone clockwise label order, starting immediately clockwise of the
// N4|N5 base line (true North), matching "32 ZONES VEDIC.pdf".
export const ZONE32_ORDER = [
  'N5', 'N6', 'N7', 'N8',
  'E1', 'E2', 'E3', 'E4', 'E5', 'E6', 'E7', 'E8',
  'S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8',
  'W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7', 'W8',
  'N1', 'N2', 'N3', 'N4',
];

const SECTOR_ANGLE_32 = 360 / 32; // 11.25
const SECTOR_ANGLE_64 = 360 / 64; // 5.625

// ---------------------------------------------------------------------------
// 1) 32 ZONES VEDIC — 32 rays, 11.25° apart, directional labels
// ---------------------------------------------------------------------------
function build32Zones() {
  const lines = [];
  const labels = [];
  for (let i = 0; i < 32; i++) {
    const angle = i * SECTOR_ANGLE_32;
    const pt = squareRayPoint(angle);
    lines.push({ x1: CX, y1: CY, x2: pt.x, y2: pt.y, stroke: '#2255cc', width: 1.1 });
    const midAngle = angle + SECTOR_ANGLE_32 / 2;
    const labelPt = squareRayPoint(midAngle, R * 0.82);
    labels.push({ x: labelPt.x, y: labelPt.y, text: ZONE32_ORDER[i], fontSize: 15, color: '#111111', align: 'center' });
  }
  const outerSquare = [
    { x: CX - R, y: CY - R }, { x: CX + R, y: CY - R },
    { x: CX + R, y: CY + R }, { x: CX - R, y: CY + R },
  ];
  return {
    title: '32 Zones',
    subtitle: 'From center, 11.25° angle each — line between N4 and N5 is the base line',
    outerSquare,
    lines,
    labels,
    compassLabels: compassOuterLabels(),
  };
}

// ---------------------------------------------------------------------------
// 2) 64 GRIDING — 64 rays, 5.625° apart, numbered 1-64
// ---------------------------------------------------------------------------
function build64Grid() {
  const lines = [];
  const labels = [];
  for (let i = 0; i < 64; i++) {
    const angle = i * SECTOR_ANGLE_64;
    const pt = squareRayPoint(angle);
    lines.push({ x1: CX, y1: CY, x2: pt.x, y2: pt.y, stroke: '#2255cc', width: 0.8 });
    const midAngle = angle + SECTOR_ANGLE_64 / 2;
    const labelPt = squareRayPoint(midAngle, R * 0.9);
    labels.push({ x: labelPt.x, y: labelPt.y, text: String(i + 1), fontSize: 10, color: '#111111', align: 'center' });
  }
  const outerSquare = [
    { x: CX - R, y: CY - R }, { x: CX + R, y: CY - R },
    { x: CX + R, y: CY + R }, { x: CX - R, y: CY + R },
  ];
  return {
    title: '64 Grid',
    subtitle: 'From center, 5.625° angle each — line between N4 and N5 is the base line',
    outerSquare,
    lines,
    labels,
    compassLabels: compassOuterLabels(),
  };
}

// ---------------------------------------------------------------------------
// Shared: nested-square "Padvinyash" concentric grid (used by both 64 & 81)
// Simplified to exactly what it should be: 4 concentric boundary squares,
// painted outer→inner (painter's algorithm) so each ring reads as a plain
// coloured band — no radial spokes, no direction labels, no compass lines.
// ---------------------------------------------------------------------------
function buildPadvinyash({ colored }) {
  // 4 boundary squares, outer → inner. Painting the smaller one on top of the
  // larger one automatically leaves a coloured "ring" visible around it.
  const ringHalfSizes = [R, R * 0.75, R * 0.5, R * 0.25];
  const ringFills = colored ? ['#f4a3a3', '#a8d9a8', '#f7e79a', '#a9c3ea'] : [null, null, null, null];

  const ringSquares = ringHalfSizes.map((hs) => [0, 1, 2, 3].map((k) => {
    // corners at NE(45), SE(135), SW(225), NW(315) of a square with half-size hs
    const angle = 45 + k * 90;
    return squareRayPoint(angle, hs);
  }));

  // paint order: index 0 = outermost (painted first), last = innermost (painted last, on top)
  const bands = ringSquares.map((points, i) => ({ points, fill: ringFills[i] }));

  return { ringSquares, bands, colored };
}

function build64Padvinyash() {
  // NOTE: layer id says "64" but the display name (layerNames.js) is
  // "81 Padvinyash Analysis" — the two report names were swapped per
  // explicit user request; kept here for consistency.
  const g = buildPadvinyash({ colored: false });
  return {
    title: '81 Padvinyash Analysis',
    subtitle: '32 zone boundary divided into 4 concentric bands (outer → inner)',
    ...g,
  };
}

function build81Padvinyash() {
  const g = buildPadvinyash({ colored: true });
  return {
    title: '64 Padvinyash Analysis',
    subtitle: '32 zone boundary divided into 4 concentric colour bands — innermost is the Brahmasthan',
    ...g,
  };
}

// ---------------------------------------------------------------------------
// 5) ELEMENTAL ZONE (VEDIC) — Air / Water / Fire / Earth quadrants + Brahmasthan
// ---------------------------------------------------------------------------
const ELEMENT_ZONES = [
  { name: 'AIR', color: '#8fbf82', rayStart: 25, rayEnd: 29 },   // W5/W6 axis -> N1/N2 axis
  { name: 'WATER', color: '#7fa8d9', rayStart: 1, rayEnd: 5 },   // N5/N6 axis -> E1/E2 axis
  { name: 'FIRE', color: '#e07a6b', rayStart: 9, rayEnd: 13 },   // E5/E6 axis -> S1/S2 axis
  { name: 'EARTH', color: '#e8d977', rayStart: 17, rayEnd: 21 }, // S5/S6 axis -> W1/W2 axis
];

function buildElementalZone() {
  const brahmaHalf = R / 3;
  const outerCorners = [45, 135, 225, 315]; // NE, SE, SW, NW at full R

  const zones = ELEMENT_ZONES.map((z) => {
    const angleStart = z.rayStart * SECTOR_ANGLE_32;
    const angleEnd = z.rayEnd * SECTOR_ANGLE_32;
    const innerStart = squareRayPoint(angleStart, brahmaHalf);
    const outerStart = squareRayPoint(angleStart, R);
    const outerEnd = squareRayPoint(angleEnd, R);
    const innerEnd = squareRayPoint(angleEnd, brahmaHalf);

    // include any square corner(s) that fall strictly between angleStart/angleEnd
    const midCorners = outerCorners
      .filter((c) => c > angleStart && c < angleEnd)
      .sort((a, b) => a - b)
      .map((c) => squareRayPoint(c, R));

    const points = [innerStart, outerStart, ...midCorners, outerEnd, innerEnd];
    const midAngle = (angleStart + angleEnd) / 2;
    const labelPt = squareRayPoint(midAngle, (R + brahmaHalf) / 2);
    return { points, fill: z.color, label: z.name, labelPt };
  });

  const brahmaSquare = [0, 1, 2, 3].map((k) => squareRayPoint(45 + k * 90, brahmaHalf));

  const axisLines = [];
  for (let i = 0; i < 32; i += 4) { // main 8 axis rays only (every 4th of the 32)
    const angle = i * SECTOR_ANGLE_32;
    const outer = squareRayPoint(angle, R);
    const inner = squareRayPoint(angle, brahmaHalf);
    axisLines.push({ x1: inner.x, y1: inner.y, x2: outer.x, y2: outer.y, stroke: '#555555', width: 0.8 });
  }

  const zoneLabels = [];
  for (let i = 0; i < 32; i++) {
    const midAngle = i * SECTOR_ANGLE_32 + SECTOR_ANGLE_32 / 2;
    const labelPt = squareRayPoint(midAngle, R * 0.95);
    zoneLabels.push({ x: labelPt.x, y: labelPt.y, text: ZONE32_ORDER[i], fontSize: 8, color: '#222222', align: 'center' });
  }

  return {
    title: 'Elemental Zone (Vedic)',
    subtitle: 'Air / Water / Fire / Earth zones around the central Brahmasthan (space)',
    zones,
    brahmaSquare,
    axisLines,
    zoneLabels,
  };
}

// ---------------------------------------------------------------------------
// 6) VEDIC PLANET ZONING — fixed 3x3 Disha-Lord Numero grid
// ---------------------------------------------------------------------------
const PLANET_GRID = [
  ['Moon (2)', 'Mercury (5)', 'Jupiter (3)'],
  ['Saturn (8)', '', 'Sun (1)'],
  ['Rahu Ketu (4)', 'Mars (9)', 'Venus (6)'],
];

function buildPlanetZoning() {
  const half = R * 0.75;
  const cellSize = (half * 2) / 3;
  const startX = CX - half;
  const startY = CY - half;
  const cells = [];
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 3; col++) {
      const text = PLANET_GRID[row][col];
      cells.push({
        x: startX + col * cellSize,
        y: startY + row * cellSize,
        w: cellSize,
        h: cellSize,
        text,
        isCenter: text === '',
      });
    }
  }
  return {
    title: 'Vedic Planet Zoning',
    subtitle: '9x9 Disha Lord Numero Grid — reference point 3x3 grid',
    half,
    startX,
    startY,
    cellSize,
    cells,
    table: [
      ['NORTH', 'Mercury', 5], ['NE', 'Jupiter', 3], ['EAST', 'Sun', 1], ['SE', 'Venus', 6],
      ['SOUTH', 'Mars', 9], ['SW', 'Rahu, Ketu', 4], ['WEST', 'Saturn', 8], ['NW', 'Moon', 2],
    ],
  };
}

// ---------------------------------------------------------------------------
function compassOuterLabels() {
  return [
    { angle: 0, text: 'NORTH', color: '#0033cc' },
    { angle: 45, text: 'NE', color: '#0033cc' },
    { angle: 90, text: 'EAST', color: '#118833' },
    { angle: 135, text: 'SE', color: '#cc2222' },
    { angle: 180, text: 'SOUTH', color: '#cc2222' },
    { angle: 225, text: 'SW', color: '#ccaa00' },
    { angle: 270, text: 'WEST', color: '#888888' },
    { angle: 315, text: 'NW', color: '#888888' },
  ].map((d) => ({ ...d, pt: squareRayPoint(d.angle, R + 45) }));
}

// ---------------------------------------------------------------------------
// NOTE: these exact IDs already existed as placeholder display-name entries
// in constants/layerNames.js (planned ahead of time) — reused here as-is so
// naming stays consistent across the codebase. The two Padvinyash reports had
// no placeholder yet, so they follow the same 'Vedic-<thing>' convention.
export const VEDIC_GENERATIVE_IDS = [
  'Vedic-zones-32',
  'Vedic-numbers-64',
  'Vedic-64-padvinyash',
  'Vedic-81-padvinyash',
  'Vedic-elemental-zone',
  'Vedic-planet-zoning',
];

export function buildVedicGenerativeGeometry(layerId) {
  switch (layerId) {
    case 'Vedic-zones-32': return build32Zones();
    case 'Vedic-numbers-64': return build64Grid();
    case 'Vedic-64-padvinyash': return build64Padvinyash();
    case 'Vedic-81-padvinyash': return build81Padvinyash();
    case 'Vedic-elemental-zone': return buildElementalZone();
    case 'Vedic-planet-zoning': return buildPlanetZoning();
    default: return null;
  }
}

// ============================================================================
// Canvas rasterizer — draws the geometry onto an offscreen <canvas> and
// returns a PNG data URL. This lets these 6 reports be dropped straight into
// the app's existing "uploadedImages[layerId]" pipeline (same as the
// Planning Guide's predefined PNGs), so the user gets the exact same
// drag / scale / rotate / opacity edit experience as every other report,
// instead of a separate bespoke editor. Browser-only (uses document.createElement).
// ============================================================================
export function renderVedicGeometryToDataURL(layerId, pxSize = 1400) {
  const geo = buildVedicGenerativeGeometry(layerId);
  if (!geo || typeof document === 'undefined') return null;

  const canvas = document.createElement('canvas');
  canvas.width = pxSize;
  canvas.height = pxSize;
  const ctx = canvas.getContext('2d');
  const S = VEDIC_GEN_SIZE;
  const k = pxSize / S; // unit-space -> pixel-space scale
  const pt = (p) => ({ x: p.x * k, y: p.y * k });

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, pxSize, pxSize);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const strokePoly = (points, close = true) => {
    if (!points || points.length < 2) return;
    ctx.beginPath();
    const p0 = pt(points[0]);
    ctx.moveTo(p0.x, p0.y);
    for (let i = 1; i < points.length; i++) {
      const p = pt(points[i]);
      ctx.lineTo(p.x, p.y);
    }
    if (close) ctx.closePath();
    ctx.stroke();
  };
  const fillPoly = (points, fillStyle) => {
    if (!points || points.length < 3) return;
    ctx.beginPath();
    const p0 = pt(points[0]);
    ctx.moveTo(p0.x, p0.y);
    for (let i = 1; i < points.length; i++) {
      const p = pt(points[i]);
      ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
    ctx.fillStyle = fillStyle;
    ctx.fill();
  };

  // 32-zones / 64-grid style
  if (geo.outerSquare) {
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2.5 * k;
    strokePoly(geo.outerSquare);
  }
  if (geo.lines) {
    geo.lines.forEach((l) => {
      const p1 = pt({ x: l.x1, y: l.y1 }), p2 = pt({ x: l.x2, y: l.y2 });
      ctx.strokeStyle = l.stroke;
      ctx.lineWidth = l.width * k;
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.stroke();
    });
  }
  if (geo.labels) {
    ctx.fillStyle = '#111111';
    geo.labels.forEach((lb) => {
      const p = pt(lb);
      ctx.fillStyle = lb.color;
      ctx.font = `${lb.fontSize * k}px Arial`;
      ctx.fillText(lb.text, p.x, p.y);
    });
  }
  if (geo.compassLabels) {
    geo.compassLabels.forEach((c) => {
      const p = pt(c.pt);
      ctx.fillStyle = c.color;
      ctx.font = `bold ${20 * k}px Arial`;
      ctx.fillText(c.text, p.x, p.y);
    });
  }

  // padvinyash: 4 concentric boundary squares only — paint outer→inner
  // (painter's algorithm) so each smaller square covers the middle of the
  // one before it, leaving a plain coloured ring visible. No spokes, no labels.
  if (geo.bands) {
    geo.bands.forEach((b) => {
      if (b.fill) fillPoly(b.points, b.fill);
    });
  }
  if (geo.ringSquares) {
    geo.ringSquares.forEach((sq, i) => {
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = (i === 0 ? 2.5 : 1.4) * k;
      strokePoly(sq);
    });
  }

  // elemental zone style
  if (geo.zones) {
    geo.zones.forEach((z) => {
      fillPoly(z.points, z.fill + 'CC'); // slight transparency via 8-digit hex alpha
      ctx.strokeStyle = z.fill;
      ctx.lineWidth = 1.5 * k;
      strokePoly(z.points);
      const p = pt(z.labelPt);
      ctx.fillStyle = '#222222';
      ctx.font = `bold ${16 * k}px Arial`;
      ctx.fillText(z.label, p.x, p.y);
    });
  }
  if (geo.brahmaSquare) {
    fillPoly(geo.brahmaSquare, '#ffffff');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1.8 * k;
    strokePoly(geo.brahmaSquare);
    ctx.fillStyle = '#333333';
    ctx.font = `bold ${11 * k}px Arial`;
    ctx.fillText('Brahmasthan', (S / 2) * k, (S / 2) * k);
  }
  if (geo.axisLines) {
    ctx.strokeStyle = '#555555';
    ctx.lineWidth = 0.6 * k;
    geo.axisLines.forEach((l) => {
      const p1 = pt({ x: l.x1, y: l.y1 }), p2 = pt({ x: l.x2, y: l.y2 });
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.stroke();
    });
  }
  if (geo.zoneLabels) {
    geo.zoneLabels.forEach((lb) => {
      const p = pt(lb);
      ctx.fillStyle = lb.color;
      ctx.font = `${lb.fontSize * k}px Arial`;
      ctx.fillText(lb.text, p.x, p.y);
    });
  }

  // planet zoning 3x3 grid
  if (geo.cells && geo.cells[0] && geo.cells[0].w) {
    geo.cells.forEach((c) => {
      const p0 = pt({ x: c.x, y: c.y });
      const w = c.w * k, h = c.h * k;
      ctx.fillStyle = c.isCenter ? '#F5F7FA' : '#ffffff';
      ctx.fillRect(p0.x, p0.y, w, h);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1.8 * k;
      ctx.strokeRect(p0.x, p0.y, w, h);
      if (c.text) {
        ctx.fillStyle = '#1A1F36';
        ctx.font = `bold ${13 * k}px Arial`;
        ctx.fillText(c.text, p0.x + w / 2, p0.y + h / 2);
      }
    });
  }

  // Title strip at the bottom so the exported image is self-explanatory
  ctx.fillStyle = '#1A1F36';
  ctx.font = `bold ${18 * k}px Arial`;
  ctx.textAlign = 'center';
  ctx.fillText(geo.title, (S / 2) * k, (S - 18) * k);

  return canvas.toDataURL('image/png');
}

// Generates all 6 diagrams as data URLs, keyed by layer ID — merge this into
// the app's `uploadedImages` initial state so these reports behave exactly
// like any other predefined-image report (drag / scale / rotate / opacity).
export function generateAllVedicGenerativeImages(pxSize = 1400) {
  const out = {};
  VEDIC_GENERATIVE_IDS.forEach((id) => {
    const url = renderVedicGeometryToDataURL(id, pxSize);
    if (url) out[id] = url;
  });
  return out;
}
